// ...existing code...
import { usePersonnel } from 'features/Responder/features/Personnel';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { displayUserTimeZone } from 'utils/dateUtils';
import { getScheduleEventProps } from 'utils/recurrenceUtils';
import { useAssignments } from '../../../../contexts/AssignmentsContext';
import { AssignmentAssigner } from '../AssignmentAssigner';

export const OccurrenceCard = ({
  event,
  source = '',
  calendarProps = {
    calendarView: '',
    handleViewChange: () => {},
    setCurrentDate: () => {},
    handleSelectEvent: () => {},
  },
  opacity = 1,
  isSelected = false,
  showAssigner = false,
}) => {
  const { activeEditAssignments, assignments } = useAssignments();
  const { personnel } = usePersonnel();

  // Personnel assigned to this schedule (read-only view).
  const assignedPersonnel = useMemo(() => {
    if (showAssigner || !event?.definitionId || !assignments || !personnel) return [];
    return Object.entries(assignments)
      .filter(([, list]) => list.some((a) => a.definition_id === event.definitionId))
      .map(([personnelId]) => personnel[personnelId])
      .filter(Boolean);
  }, [showAssigner, event?.definitionId, assignments, personnel]);

  // Determine if this event is currently being edited in the AssignmentBuilder.
  const thisIsSelected =
    activeEditAssignments &&
    event &&
    Object.values(activeEditAssignments).some((userList) => {
      // console.log('Comparing assignment event ID with current event ID:', userList, event);
      return userList.some((assignment) => assignment.start === event.start);
    });
  if (!event) {
    return null;
  }

  // Get styling and other props for the event based on its recurrence pattern and other factors.
  const eventProps = getScheduleEventProps(event);

  // Use user's time zone or fallback to system
  const userTimeZone = DateTime.local().zoneName;

  // Custom rendering for month view to ensure events are visible and clickable, since month view typically shows a condensed version of events.
  const CustomMonthEvent = ({ event }) => (
    <div
      role="button"
      onClick={(e) => {
        e.stopPropagation();
        // If a custom handler is provided, use it. Otherwise, default to navigating to day view of the event's start date.
        if (calendarProps.handleSelectEvent) {
          calendarProps.handleSelectEvent(event);
          return;
        }
        calendarProps.setCurrentDate(event.start);
        calendarProps.handleViewChange('day');
      }}
      className="d-flex align-items-center rounded-1 p-1 m-1 mb-2"
      style={{
        background: '#325277',
        ...eventProps.style,
        opacity: opacity,
        border: thisIsSelected ? '2px solid #f3ffff' : 'none',
        outline: isSelected ? '2px solid white' : 'none',
        zIndex: 1,
      }}
    >
      <div
        className="rounded-1 flex-shrink-0"
        style={{
          background: eventProps.style['--bar-color'] || eventProps.style.barColor,
        }}
      />
      <span className="text-truncate" style={{ minWidth: 0 }}>
        {event.title || 'Event'}
      </span>
    </div>
  );

  if (calendarProps.calendarView === 'month') {
    return <CustomMonthEvent event={event} />;
  }

  return (
    <div
      className="w-100 h-100 p-1"
      style={isSelected ? { outline: '2px solid white', outlineOffset: '-2px' } : undefined}
    >
      <div
        className={`${eventProps.className} text-white rounded-2 w-100 h-100 overflow-hidden`}
        style={{
          background: eventProps.style.backgroundColor || '#325a7d',
          padding: '4px 24px',
          textOverflow: 'ellipsis',
          ...eventProps.style,
          opacity: opacity,
        }}
      >
        <div className="text-truncate">
          {event.start &&
            DateTime.fromJSDate(event.start, { zone: userTimeZone }).toFormat(
              `${!source.includes('pattern') ? 'MM/dd/yyyy, ' : ''} hh:mm a`,
            )}{' '}
          -{' '}
          {event.end && DateTime.fromJSDate(event.end, { zone: userTimeZone }).toFormat('hh:mm a')}{' '}
          {displayUserTimeZone()}
        </div>
        <div className="fw-semibold fs-5 mb-1 pb-1 text-truncate">{event.title}</div>
        {event.info && (
          <div
            className="mt-1 overflow-hidden"
            style={{ color: '#b0c4de', textOverflow: 'ellipsis' }}
          >
            {event.info}
          </div>
        )}
        {showAssigner ? (
          <AssignmentAssigner event={event} source={source} />
        ) : (
          <div className="mt-2">
            <div className="mb-1">Assigned:</div>
            {assignedPersonnel.length > 0
              ? assignedPersonnel.map((p) => <div key={p.userId}>{p.name}</div>)
              : 'None assigned'}
          </div>
        )}
      </div>
    </div>
  );
};
